import * as Sentry from "@sentry/react";
import { Controller, FieldValues, Path, PathValue, useFormContext } from "react-hook-form";
import { QueryKey } from "@tanstack/react-query";
import { Fragment } from "react";
import { Stack, Table as MuiTable, Typography } from "@mui/material";
import SimpleBar from "simplebar-react";
import { groupBy } from "lodash";

import { TableBody, TableHead } from "@/components/elements/table/components";
import { useGet, UseGetOptions } from "@/hooks";
import { ERROR_BOUNDARY_MESSAGE } from "@/constants";
import { Loader, TableProps } from "@/components";

type TableGroup<TRecord> = Pick<TableProps<TRecord>, "header" | "data">[];

export type FormAsyncCheckBoxTableValue = Array<string | number>;

export type FormAsyncCheckBoxTableProps<TFormValues extends FieldValues, TRecord> = Pick<
  TableProps<TRecord>,
  "columns" | "getTableRecordId"
> & {
  name: Path<TFormValues>;
  endpoint: string;
  queryKey?: QueryKey;
  getOptions?: UseGetOptions<TRecord[]>;
  getGroupBy?: (record: TRecord) => string;
};

function getDataGroups<TRecord>(data: TRecord[] = [], getGroupBy?: (record: TRecord) => string): TableGroup<TRecord> {
  if (!getGroupBy) {
    return [{ data }];
  }

  return Object.entries(groupBy(data, getGroupBy)).map(([header, tableData]) => ({ header, data: tableData }));
}

export function FormAsyncCheckBoxTable<TFormValues extends FieldValues, TRecord>({
  name,
  endpoint,
  queryKey,
  getOptions = {},

  // table props
  columns,
  getTableRecordId,
  getGroupBy,
}: FormAsyncCheckBoxTableProps<TFormValues, TRecord>) {
  const { control, setValue, watch } = useFormContext<TFormValues>();
  const selected = (watch(name) ?? []) as FormAsyncCheckBoxTableValue;

  const {
    data = [],
    error,
    isLoading,
  } = useGet<TRecord[]>({
    endpoint,
    queryKey,
    options: getOptions,
  });

  const tableGroups = getDataGroups(data, getGroupBy);

  const handleCheckboxChange = (checked: boolean, record: TRecord) => {
    setValue(
      name,
      (checked
        ? [...selected, getTableRecordId(record)]
        : [...selected].filter((id) => id !== getTableRecordId(record))) as PathValue<TFormValues, Path<TFormValues>>
    );
  };

  return (
    <Sentry.ErrorBoundary fallback={<div>{ERROR_BOUNDARY_MESSAGE.component}</div>}>
      <Loader data={data} error={error} isLoading={isLoading}>
        <Controller
          name={name}
          control={control}
          render={({ field }) => (
            <Stack>
              {tableGroups.map(({ header: title, data: tableData }, index) => (
                <Fragment key={index}>
                  {title && <Typography variant="overline">{title}</Typography>}

                  <SimpleBar
                    style={{
                      minHeight: "initial",
                      maxHeight: "initial",
                    }}
                    autoHide={false}
                  >
                    <MuiTable size="medium" stickyHeader>
                      <TableHead
                        data={tableData}
                        selected={tableData.filter((record) => field.value.includes(getTableRecordId(record)))}
                        columns={columns}
                        options={{
                          selectable: true,
                          showSelectAll: false,
                        }}
                      />
                      <TableBody
                        data={tableData}
                        columns={columns}
                        selected={tableData.filter((record) => field.value.includes(getTableRecordId(record)))}
                        getTableRecordId={getTableRecordId}
                        onSelect={handleCheckboxChange}
                        options={{
                          selectable: true,
                        }}
                      />
                    </MuiTable>
                  </SimpleBar>
                </Fragment>
              ))}
            </Stack>
          )}
        />
      </Loader>
    </Sentry.ErrorBoundary>
  );
}
